import React, { useEffect, useState } from "react";
import moment from "moment";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import CopyButton from "../global/CopyButton";

/*
    API Access (static#49) — D15, the ONE-TIME reveal.

    This modal is the delivery mechanism for the customer's credentials. AWS does
    not let anyone retrieve an IAM secret access key after creation — not
    Golfbert, not support, not AWS — so if this modal is dismissed without the
    secret being saved, the customer's only remedy is to rotate and start over.

    Consequences for this component, all deliberate:
      * `credentials` arrives as a prop from the parent's LOCAL STATE. It is
        never read from Redux, because src/store/index.js persists state to
        localStorage under a hardcoded AES key.
      * The modal is `backdrop="static"` with `keyboard={false}` and has no close
        "x" — a stray click on the page or an Escape keypress must not be able to
        destroy the only copy of the secret.
      * Dismissal requires ticking an explicit acknowledgement.
      * Nothing here logs. Do not add a console statement to this file.

    FIELD ORDER IS DELIBERATE. API Key first, then Access Key, then Secret
    Access Key, with the "shown once" warning directly BELOW the secret rather
    than at the top of the modal. A warning above the fields is read before
    there is anything to apply it to; sitting under the one value that can
    never be recovered, it is next to the thing it is about.
*/

/** One labelled, read-only credential with its own copy button. */
const CredentialField = ({ id, label, value, badge, help, ariaLabel }) => (
  <div className="api-access__credential mb-3">
    <label className="gb-label" htmlFor={id}>
      {label}
      {badge}
    </label>
    <div className="d-flex align-items-center">
      <input
        id={id}
        className="gb-field api-access__credential-input"
        type="text"
        value={value}
        readOnly
        spellCheck={false}
        autoComplete="off"
        onFocus={(e) => e.target.select()}
      />
      <CopyButton
        value={value}
        className="ml-2 flex-shrink-0"
        ariaLabel={ariaLabel}
      />
    </div>
    {help && <p className="gb-help">{help}</p>}
  </div>
);

const ApiKeyReveal = ({
  isOpen,
  credentials,
  mode = "provision",
  prevExpiresAt = null,
  onAcknowledge,
}) => {
  const [acknowledged, setAcknowledged] = useState(false);

  // Reset the acknowledgement each time the modal opens, so the checkbox can
  // never arrive pre-ticked for a later reveal.
  useEffect(() => {
    if (isOpen) {
      setAcknowledged(false);
    }
  }, [isOpen]);

  const accessKeyId = credentials?.accessKeyId || "";
  const secretAccessKey = credentials?.secretAccessKey || "";
  const apigwKeyValue = credentials?.apigwKeyValue || "";

  // Same order as the fields, and the same label the field carries.
  const allFields = [
    `API Key (x-api-key): ${apigwKeyValue}`,
    `Access Key: ${accessKeyId}`,
    `Secret Access Key: ${secretAccessKey}`,
  ].join("\n");

  const isRotation = mode === "rotate";
  const previousExpiry =
    prevExpiresAt && moment(prevExpiresAt).isValid()
      ? moment(prevExpiresAt).format("MMMM D, YYYY")
      : null;

  return (
    <Modal
      className="contact-modal api-access-reveal"
      isOpen={!!isOpen}
      backdrop="static"
      keyboard={false}
      autoFocus
    >
      {/* No `toggle` prop: this header intentionally has no close "x". */}
      <ModalHeader className="contact-modal__head">
        <h4>
          {isRotation ? "Your new API credentials" : "Your API credentials"}
        </h4>
      </ModalHeader>
      <ModalBody className="contact-modal__modal-scrollable">
        <p className="mb-4">
          {isRotation ? (
            <>
              Use these credentials from now on.
              {previousExpiry ? (
                <>
                  {" "}
                  Your previous credentials keep working until{" "}
                  <strong>{previousExpiry}</strong>, so you have time to deploy
                  the change.
                </>
              ) : (
                <>
                  {" "}
                  Your previous credentials keep working for 7 more days, so you
                  have time to deploy the change.
                </>
              )}
            </>
          ) : (
            <>
              Requests to <code className="gb-code">api.golfbert.com</code> need
              all three of these: the Access Key and Secret Access Key sign the
              request, and the API Key goes in the{" "}
              <code className="gb-code">x-api-key</code> header.
            </>
          )}
        </p>

        <CredentialField
          id="api-access-reveal-apigw-key"
          label={
            <>
              API Key (<code className="gb-code">x-api-key</code>)
            </>
          }
          value={apigwKeyValue}
          ariaLabel="Copy API Key"
          help="You can see this one again on your API Management page at any time. The Secret Access Key you cannot."
        />

        <CredentialField
          id="api-access-reveal-access-key-id"
          label="Access Key"
          value={accessKeyId}
          ariaLabel="Copy Access Key"
        />

        <CredentialField
          id="api-access-reveal-secret"
          label="Secret Access Key"
          badge={
            <span className="api-access__badge api-access__badge--danger ml-2">
              shown once
            </span>
          }
          value={secretAccessKey}
          ariaLabel="Copy Secret Access Key"
        />

        {/* Directly under the secret it is warning about. */}
        <div className="gb-alert mb-4" role="alert">
          <p className="gb-alert__title">
            This is the only time your Secret Access Key will be shown.
          </p>
          <p className="mb-0">
            Golfbert does not store it and cannot show it to you again. Copy it
            somewhere safe before you close this window. If you lose it, you
            will have to rotate your key and update your integration.
          </p>
        </div>

        <div className="mb-4">
          <CopyButton
            value={allFields}
            variant="primary"
            label="Copy all three"
            copiedLabel="All three copied"
            ariaLabel="Copy all three credentials"
          />
        </div>

        <div className="api-access__ack">
          <input
            type="checkbox"
            id="api-access-reveal-ack"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
          />
          <label htmlFor="api-access-reveal-ack" className="mb-0">
            I have saved my Secret Access Key somewhere safe. I understand it
            cannot be shown again.
          </label>
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          className="gb-btn gb-btn--primary"
          onClick={onAcknowledge}
          disabled={!acknowledged}
        >
          Done
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default ApiKeyReveal;
